import { Grid, useMediaQuery } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { useAccount } from "wagmi";

import { Breadcrumbs, PageHeader } from "@dzambalaorg/mui-page-layout";
import type { IPaginationResult } from "@dzambalaorg/types-collection";
import { useDialogView, useFetchQuery } from "@dzambalaorg/react-hooks";
import { ListAction, ListActions, ListItem, StyledPagination } from "@framework/styled";
import { ContractStatus, IRaffleRound } from "@framework/types";
import { RaffleReleaseButton, RaffleRoundEndButton, WithCheckPermissionsListWrapper } from "@framework/shared";
import { List } from "@dzambalaorg/ui-kit";

import { RaffleRoundDialogView } from "./view";

const RAFFLE_ROUNDS_URL = "/raffle/rounds";
const RAFFLE_ROUNDS_DIALOG_KEY = "raffleRounds";

export const RaffleRounds = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { data, isError, isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } =
    useFetchQuery<IPaginationResult<IRaffleRound>>(RAFFLE_ROUNDS_URL, { enableInfiniteScroll: isMd });

  const { viewItem, handleView, viewDialogUniqueKey, closeViewDialog, viewMode } = useDialogView<IRaffleRound>();

  const { address: account = "" } = useAccount();

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "raffle", "raffle.rounds"]} />

      <PageHeader message="pages.raffle.rounds.title" />

      <WithCheckPermissionsListWrapper
        isLoading={!data && isLoading}
        isError={!data && isError}
        isEmpty={allRows.length === 0}
        count={allRows.length}
      >
        {allRows.map(round => (
          <ListItem key={round.id} account={account} contract={round.contract}>
            <List.ItemText sx={{ width: 0.2 }}>
              {round.contract?.title} #{round.roundId}
            </List.ItemText>
            <ListActions>
              <ListAction
                onClick={handleView(RAFFLE_ROUNDS_DIALOG_KEY, round, "view")}
                message="form.tips.view"
                icon={Visibility}
              />
              <RaffleReleaseButton round={round} onRefreshPage={refetch} />
              <RaffleRoundEndButton
                contract={round.contract}
                disabled={
                  round.contract!.contractStatus === ContractStatus.INACTIVE ||
                  round.contract!.parameters.roundId !== round.id
                }
              />
            </ListActions>
          </ListItem>
        ))}
      </WithCheckPermissionsListWrapper>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <RaffleRoundDialogView
        onCancel={closeViewDialog}
        onConfirm={closeViewDialog}
        open={viewDialogUniqueKey === `${RAFFLE_ROUNDS_DIALOG_KEY}.${viewMode}`}
        viewItem={viewItem}
      />
    </Grid>
  );
};
